import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useSearchParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faRefresh } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import InventoryVendor from './PredictInventoryVendor';
import PredictProjectFilter from './PredictProjectFilter';
import { findProduct } from '../bz/products';
import { IcoExternal, IcoSearch, IcoX } from '../bz/icons';
import { VendorMark } from '../bsa/projects/ProjectAtoms';
import Pager from '../bsa/projects/Pager';
import '../bsa/projects/projectsList.css';
import './predictThreshold.css';
import {
  DEFAULT_SORT,
  PROMOTION_SCORE,
  THRESHOLD_RANGE,
  bandIndex,
  bugKey,
  bugPagePath,
  csvFileName,
  cveId,
  filterBugs,
  formatCount,
  formatDate,
  formatDateTime,
  formatScore,
  isPromoted,
  isScore,
  projectsOf,
  minScoreOptions,
  parseMin,
  projectIds,
  resolveProjects,
  resolveWeaknesses,
  safeUrl,
  scalePosition,
  scoreTone,
  sortBugs,
  sourceLabel,
  thresholdKind,
  toCsv,
  trackedNumbers,
  weaknessLabel,
  weaknessesOf
} from './predictWatchlistModel';

// Where the customer's threshold is set: Predict's Settings page (STRY0038971).
export const SETTINGS_PATH = '/predict/settings';

const NO_FILTERS = { query: '', projects: [], weaknesses: [], min: null };

// The list shows this many bugs a page; a new search, filter or sort goes back to page 1.
export const PAGE_SIZE = 25;

// The filters a link to the list carries: ?project=<bug.vendor> (repeatable), q=, min=,
// weakness=<bug.weaknessType> (repeatable). Projects and weakness types resolve
// against the list once it loads.
const filtersFromUrl = params => ({
  query: params.get('q') || '',
  projects: params.getAll('project'),
  weaknesses: params.getAll('weakness'),
  min: parseMin(params.get('min'))
});

const URL_KEYS = ['project', 'q', 'min', 'weakness'];

const small = { fontSize: 'var(--fs-sm)' };

const linkButton = {
  background: 'none',
  border: 0,
  padding: 0,
  color: 'var(--info)',
  cursor: 'pointer',
  font: 'inherit'
};

const bugShape = PropTypes.shape({
  vendor: PropTypes.string.isRequired,
  bugId: PropTypes.string.isRequired,
  title: PropTypes.string,
  url: PropTypes.string,
  filed: PropTypes.string,
  score: PropTypes.number,
  promoted: PropTypes.bool,
  reason: PropTypes.string,
  target: PropTypes.string,
  targetName: PropTypes.string,
  inventoryVendorId: PropTypes.string,
  inventoryProducts: PropTypes.arrayOf(PropTypes.string)
});

const TONE_STYLE = {
  bad: { background: 'var(--bad-soft)', color: 'var(--bad)' },
  prevent: { background: 'var(--prevent-soft)', color: 'var(--prevent-ink)' },
  warn: { background: 'var(--warn-soft)', color: 'var(--warn)' },
  muted: { background: 'var(--line-soft)', color: 'var(--ink-soft)' }
};

export const ScoreChip = ({ score }) => (
  <span
    className="bz-mono"
    style={{
      display: 'inline-flex',
      justifyContent: 'center',
      minWidth: 40,
      padding: '2px 7px',
      borderRadius: 'var(--r-sm)',
      fontWeight: 600,
      fontSize: 'var(--fs-md)',
      ...TONE_STYLE[scoreTone(score)]
    }}
  >
    {formatScore(score)}
  </span>
);
ScoreChip.propTypes = { score: PropTypes.number };

// The original bug, in a new tab. Only an http(s) link becomes an anchor.
export const BugLink = ({ url, children, onClick }) => {
  const href = safeUrl(url);
  if (!href) return children;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
      {children}
    </a>
  );
};
BugLink.propTypes = { url: PropTypes.string, children: PropTypes.node.isRequired, onClick: PropTypes.func };

const CvePill = ({ cve }) => {
  const id = cveId(cve);
  return id ? <span className="bz-pill bz-pill--bad bz-mono">Became {id}</span> : null;
};
CvePill.propTypes = { cve: PropTypes.oneOfType([PropTypes.string, PropTypes.object]) };

const SortHeader = ({ label, sortKey, sort, onSort }) => {
  const active = sort.key === sortKey;
  return (
    <th
      className="bz-th-sort"
      aria-sort={active ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
      style={{ width: sortKey === 'score' ? 120 : 130 }}
    >
      <button type="button" onClick={() => onSort(sortKey)}>
        {label}
        <svg
          className="bz-th-sort__chev"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </th>
  );
};
SortHeader.propTypes = {
  label: PropTypes.string.isRequired,
  sortKey: PropTypes.string.isRequired,
  sort: PropTypes.shape({ key: PropTypes.string, dir: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired
};

// What BugZero tracks (STRY0039127): BugZero's archive, the customer's
// projects' share of it, and what Predict has scored and promoted. Each number
// carries its own label; the threshold changes none of them.
const CountsBanner = ({ counts, projects }) => (
  <section
    aria-label="What BugZero tracks"
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px 28px',
      alignItems: 'center',
      padding: '18px 20px',
      background: 'linear-gradient(180deg, var(--predict-soft) 0%, var(--bg-elev) 100%)',
      border: '1px solid var(--line)',
      borderRadius: 'var(--r-lg)'
    }}
  >
    <div style={{ flex: '1 1 300px', minWidth: 0 }}>
      <div className="bz-kicker" style={{ color: 'var(--predict)' }}>
        What BugZero tracks
      </div>
      <p style={{ margin: '6px 0 0', color: 'var(--ink-soft)', fontSize: 'var(--fs-md)', maxWidth: '62ch' }}>
        BugZero&apos;s archive holds every bug it has collected from the trackers and vendors it watches. Predict scores
        each new one as it arrives, for all BugZero customers, and promotes it at a score of{' '}
        {formatScore(PROMOTION_SCORE)} or higher. Your threshold doesn&apos;t change these numbers.
      </p>
    </div>
    {/* The four numbers wrap as one group, so a narrow page never strands one beside the text. */}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 28px', flex: '0 1 auto' }}>
      {trackedNumbers(counts, projects).map(({ key, label, value }) => (
        <div key={key} role="group" aria-label={label} style={{ maxWidth: 170 }}>
          <div
            data-testid={`count-${key}`}
            style={{ fontSize: 'var(--fs-3xl)', fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCount(value)}
          </div>
          <div className="bz-stat__label" style={{ marginTop: 6 }} aria-hidden="true">
            {label}
          </div>
        </div>
      ))}
    </div>
  </section>
);
CountsBanner.propTypes = {
  counts: PropTypes.shape({
    scored: PropTypes.number,
    promoted: PropTypes.number,
    since: PropTypes.string,
    archive: PropTypes.shape({ bugs: PropTypes.number })
  }),
  projects: PropTypes.shape({ bugs: PropTypes.number })
};

const Fact = ({ label, children, note }) => (
  <>
    <dt className="bz-muted">{label}</dt>
    <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
      {note && (
        <div className="bz-muted" style={small}>
          {note}
        </div>
      )}
    </dd>
  </>
);
Fact.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, note: PropTypes.string };

// A mark this far along the scale puts its label on its left, inside the box.
const END_OF_SCALE = 75;

const ScaleMark = ({ kind, at, children }) => {
  const left = scalePosition(at);
  return (
    <div
      className={`pd-scale__mark pd-scale__mark--${kind}${left > END_OF_SCALE ? ' pd-scale__mark--end' : ''}`}
      style={{ left: `${left}%` }}
    >
      <span>{children}</span>
    </div>
  );
};
ScaleMark.propTypes = { kind: PropTypes.string.isRequired, at: PropTypes.number.isRequired, children: PropTypes.node };

// The bug's score on a 0-10 scale, with 6.0 (where every bug is promoted) and
// the customer's threshold marked. Read out as one sentence.
const ScoreScale = ({ score, threshold }) => {
  const yours = isScore(threshold) ? `; your threshold is ${formatScore(threshold)}` : '';
  return (
    <div
      className="pd-scale"
      role="img"
      data-testid="score-scale"
      aria-label={`Score ${formatScore(score)} of 10. Bugs are promoted at ${formatScore(PROMOTION_SCORE)}${yours}.`}
    >
      <div className="pd-scale__fill" style={{ width: `${scalePosition(score)}%` }} />
      <ScaleMark kind="promoted" at={PROMOTION_SCORE}>
        {formatScore(PROMOTION_SCORE)} promoted
      </ScaleMark>
      {isScore(threshold) && (
        <ScaleMark kind="yours" at={threshold}>
          Yours {formatScore(threshold)}
        </ScaleMark>
      )}
    </div>
  );
};
ScoreScale.propTypes = { score: PropTypes.number, threshold: PropTypes.number };

const FOCUSABLE = 'a[href], button:not([disabled])';

// One bug in full, from GET /predict/bug. The list row shows at once; the rest
// arrives with the detail. Escape, the close button and the backdrop close it,
// and Tab stays inside while it is open.
const BugPanel = ({ bug, threshold, onClose }) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const [detail, setDetail] = useState({ status: 'loading', bug: null });

  useEffect(() => {
    let cancelled = false;
    setDetail({ status: 'loading', bug: null });
    BZAPIService.getPredictBug(bug.vendor, bug.bugId)
      .then(response => {
        if (cancelled) return;
        setDetail(response?.bug ? { status: 'ready', bug: response.bug } : { status: 'gone', bug: null });
      })
      .catch(error => {
        console.error('Failed to load the Predict bug:', error);
        if (!cancelled) setDetail({ status: 'failed', bug: null });
      });
    return () => {
      cancelled = true;
    };
  }, [bug.vendor, bug.bugId, attempt]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const onKeyDown = event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) return;
    const focusable = [...panelRef.current.querySelectorAll(FOCUSABLE)];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const shown = detail.bug ? { ...bug, ...detail.bug } : bug;
  const promoted = isPromoted(shown);
  const href = safeUrl(shown.url);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'var(--ink)', opacity: 0.4, zIndex: 1060 }}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          outline: 'none',
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(540px, 100%)',
          background: 'var(--bg-elev)',
          borderLeft: '1px solid var(--line)',
          boxShadow: 'var(--shadow-pop)',
          zIndex: 1061,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div
          className="bz-row"
          style={{
            gap: 12,
            alignItems: 'flex-start',
            padding: '16px 20px 14px',
            borderBottom: '1px solid var(--line-soft)'
          }}
        >
          <VendorMark vendorId={shown.vendor} label={shown.targetName || shown.vendor} size={32} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="bz-kicker">
              {shown.targetName} ·{' '}
              <span className="bz-mono" style={{ textTransform: 'none', letterSpacing: 0 }}>
                {shown.bugId}
              </span>
            </div>
            <h2 id={titleId} className="bz-h2" style={{ marginTop: 4, fontSize: 18, lineHeight: 1.3 }}>
              {shown.title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            className="bz-btn bz-btn--ghost bz-btn--sm"
            style={{ padding: 6 }}
            aria-label="Close"
            onClick={onClose}
          >
            <IcoX size={15} />
          </button>
        </div>

        <div
          className="bz-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '18px 20px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 18
          }}
        >
          <div
            className="bz-row"
            style={{
              gap: 18,
              padding: '14px 16px',
              border: '1px solid var(--line-soft)',
              borderRadius: 'var(--r-md)',
              background: 'var(--bg-soft)'
            }}
          >
            <div>
              <div className="bz-kicker">Score</div>
              <div className="bz-row" style={{ alignItems: 'baseline', gap: 4 }}>
                <span className="bz-mono" style={{ fontSize: 40, fontWeight: 600, lineHeight: 1 }}>
                  {formatScore(shown.score)}
                </span>
                <span className="bz-muted bz-mono">/ 10</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {promoted ? (
                  <span className="bz-pill bz-pill--predict">
                    <span className="bz-dot" />
                    Promoted
                  </span>
                ) : (
                  <span className="bz-pill">Not promoted · shown by your threshold ({formatScore(threshold)})</span>
                )}
                <CvePill cve={shown.cve} />
              </div>
              <ScoreScale score={shown.score} threshold={threshold} />
            </div>
          </div>

          {detail.status === 'loading' && (
            <div className="text-center" style={{ padding: 24 }}>
              <Spinner color="primary" size="sm" />
              <div className="bz-muted" style={{ marginTop: 8 }}>
                Loading the bug…
              </div>
            </div>
          )}
          {detail.status === 'failed' && (
            <div className="pl-empty" style={{ padding: 24 }}>
              <div className="pl-empty__title">Couldn&apos;t load this bug.</div>
              <button type="button" className="bz-btn bz-btn--sm" onClick={() => setAttempt(n => n + 1)}>
                Try again
              </button>
            </div>
          )}
          {detail.status === 'gone' && (
            <div className="pl-empty" style={{ padding: 24 }}>
              <div className="pl-empty__title">This bug is no longer on your watch list.</div>
              <div className="bz-muted">Refresh the page to see the current list.</div>
            </div>
          )}
          {detail.status === 'ready' && (
            <>
              <div>
                <div className="bz-kicker">Rationale</div>
                <p style={{ margin: '6px 0 0', fontSize: 'var(--fs-md)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {shown.reason || '—'}
                </p>
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px minmax(0, 1fr)',
                  gap: '8px 14px',
                  fontSize: 'var(--fs-md)',
                  margin: 0
                }}
              >
                <Fact label="Weakness type" note="The scorer's defect class">
                  {weaknessLabel(shown.weaknessType)}
                </Fact>
                <Fact label="Source" note="Where the bug was filed">
                  {sourceLabel(shown.source)}
                </Fact>
                <Fact label="Filed">{formatDate(shown.filed)}</Fact>
                <Fact label="First seen" note="When BugZero first saw it">
                  {formatDate(shown.firstSeen)}
                </Fact>
                <Fact label="Frozen" note="Its first score, kept for good">
                  {formatDateTime(shown.frozenAt)}
                </Fact>
                <Fact label="Scorer version">
                  <span className="bz-mono">{shown.scorerVersion || '—'}</span>
                </Fact>
                <Fact label="Link">
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="bz-mono" style={small}>
                      {href}
                    </a>
                  ) : (
                    '—'
                  )}
                </Fact>
              </dl>
            </>
          )}

          <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
            {href && (
              <a href={href} target="_blank" rel="noopener noreferrer" className="bz-btn bz-btn--primary">
                <IcoExternal size={14} /> Open the original bug
              </a>
            )}
            <Link to={bugPagePath(shown.vendor, shown.bugId)} className="bz-btn">
              Open full page
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
};
BugPanel.propTypes = { bug: bugShape.isRequired, threshold: PropTypes.number, onClose: PropTypes.func.isRequired };

const download = (text, name) => {
  // The byte-order mark makes Excel read the file as UTF-8.
  const url = URL.createObjectURL(new Blob(['\uFEFF', text], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked a moment later: some browsers cancel a download whose URL goes at once.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Predict → Watch list (STRY0038309).
 *
 * The bugs in the customer's Predict scope that score at or above their
 * threshold, from GET /predict/watchlist. The API decides both the scope (the
 * caller's readable vendors) and the threshold, so the page sends neither. The
 * BugZero-wide counts come from GET /predict/counts; when they fail the list
 * still shows. A row opens the bug's detail (GET /predict/bug). The list is
 * paged, PAGE_SIZE bugs a page; the CSV holds every row that matches, on every
 * page, in their order.
 *
 * The threshold above the list is the organization's, as the list was read
 * at, marked Default or Custom. It is read-only here: it changes only on
 * Settings, after an admin applies it (STRY0039087).
 *
 * The filters live in the URL, so other pages can link to a filtered list:
 * `project` (repeatable, a bug's archive vendor id), `q`, `min` and `weakness`
 * (repeatable, a bug's weakness type; STRY0039127). They are read on load and
 * written back, replacing the entry, as they change. The CSV holds the rows
 * the filters leave.
 *
 * Above the list, what BugZero tracks (STRY0039127): BugZero's archive from
 * GET /predict/counts, the archive of the customer's projects from the watch
 * list's `archive`, and what Predict scored and promoted since its first run.
 *
 * Real data only; the route sits behind <ProductGuard product="predict">.
 */
const PredictWatchlist = () => {
  const [list, setList] = useState({ status: 'loading', bugs: [], threshold: null, asOf: null, archive: null });
  const [counts, setCounts] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromUrl(searchParams));
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [openBug, setOpenBug] = useState(null);
  const opener = useRef(null);
  const loadId = useRef(0);

  // The list and the counts load side by side; a Refresh started since wins.
  const load = useCallback(() => {
    const id = ++loadId.current;
    const current = () => id === loadId.current;
    setList(state => ({ ...state, status: 'loading' }));

    BZAPIService.getPredictWatchlist()
      .then(body => {
        if (!current()) return;
        const bugs = Array.isArray(body?.bugs) ? body.bugs.filter(b => b && b.vendor && b.bugId) : [];
        setList({
          status: 'ready',
          bugs,
          threshold: typeof body?.threshold === 'number' ? body.threshold : null,
          asOf: body?.asOf || null,
          archive: body?.archive || null
        });
        setFilters(f => ({
          ...f,
          projects: resolveProjects(f.projects, bugs),
          weaknesses: resolveWeaknesses(f.weaknesses, bugs)
        }));
      })
      .catch(error => {
        console.error('Failed to load the Predict watch list:', error);
        if (current()) setList({ status: 'failed', bugs: [], threshold: null, asOf: null, archive: null });
      });

    BZAPIService.getPredictCounts()
      .then(body => {
        if (current()) setCounts(body || null);
      })
      .catch(error => {
        console.error('Failed to load the Predict counts:', error);
        if (current()) setCounts(null);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kind = isScore(list.threshold) ? thresholdKind(list.threshold, THRESHOLD_RANGE.default) : null;
  const aboveDefault = isScore(list.threshold) && list.threshold > THRESHOLD_RANGE.default;

  const projects = useMemo(() => projectsOf(list.bugs), [list.bugs]);
  const weaknesses = useMemo(() => weaknessesOf(list.bugs), [list.bugs]);

  // The filters, back into the URL. Not before the list is ready: a URL's
  // projects resolve against it first.
  useEffect(() => {
    if (list.status !== 'ready') return;
    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        URL_KEYS.forEach(key => next.delete(key));
        projectIds(filters.projects, projects).forEach(id => next.append('project', id));
        if (filters.query.trim()) next.set('q', filters.query);
        if (isScore(filters.min)) next.set('min', formatScore(filters.min));
        filters.weaknesses.forEach(type => next.append('weakness', type));
        return next;
      },
      { replace: true }
    );
  }, [filters, projects, list.status, setSearchParams]);
  const rows = useMemo(() => sortBugs(filterBugs(list.bugs, filters), sort), [list.bugs, filters, sort]);
  const band = bandIndex(rows, sort);
  const belowPromotion = rows.filter(bug => !isPromoted(bug)).length;
  useEffect(() => setPage(1), [filters, sort]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const first = (current - 1) * PAGE_SIZE;
  const pageRows = rows.slice(first, first + PAGE_SIZE);
  const isLoading = list.status === 'loading';

  const toggleSort = key =>
    setSort(current =>
      current.key === key ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
    );

  // `from` is where focus goes back when the panel closes.
  const open = (bug, from) => {
    opener.current = from || document.activeElement;
    setOpenBug(bug);
  };
  const close = () => {
    setOpenBug(null);
    if (opener.current && typeof opener.current.focus === 'function') opener.current.focus();
  };

  const exportCsv = () => download(toCsv(rows), csvFileName());

  return (
    <div
      style={{
        padding: '24px 32px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}
    >
      <div className="bz-section-head" style={{ marginBottom: 0 }}>
        <div>
          <div className="bz-kicker" style={{ color: findProduct('predict').accent }}>
            Predict · Watch list
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Watch list
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            Bugs in your scope that score at or above your threshold, highest score first.
          </div>
        </div>
        <div className="bz-row" style={{ gap: 8 }}>
          <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
            <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
            {' Refresh'}
          </button>
          <button
            type="button"
            className="bz-btn bz-btn--sm bz-btn--primary"
            onClick={exportCsv}
            disabled={isLoading || rows.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} />
            {' Export CSV'}
          </button>
        </div>
      </div>

      <CountsBanner counts={counts} projects={list.archive} />

      <div className="bz-card">
        <div
          className="bz-row"
          style={{ gap: 16, flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}
        >
          <label className="bz-input pl-search">
            <IcoSearch size={14} />
            <input
              value={filters.query}
              placeholder="Search titles, IDs, rationale or CVEs"
              aria-label="Search the watch list"
              onChange={e => setFilters(f => ({ ...f, query: e.target.value }))}
            />
            {filters.query && (
              <button
                type="button"
                className="pl-search__clear"
                aria-label="Clear search"
                onClick={() => setFilters(f => ({ ...f, query: '' }))}
              >
                <IcoX size={12} />
              </button>
            )}
          </label>
          <label className="bz-input" style={{ gap: 6, margin: 0 }}>
            <span className="bz-muted" style={small} aria-hidden="true">
              Min score
            </span>
            <select
              aria-label="Minimum score"
              value={isScore(filters.min) ? formatScore(filters.min) : ''}
              onChange={e => setFilters(f => ({ ...f, min: parseMin(e.target.value) }))}
              style={{ border: 0, outline: 0, background: 'transparent', font: 'inherit' }}
            >
              <option value="">Any</option>
              {minScoreOptions(list.threshold, filters.min).map(value => (
                <option key={value} value={formatScore(value)}>
                  {formatScore(value)} or higher
                </option>
              ))}
            </select>
          </label>
          <div className="bz-grow" />
          <div className="bz-row pd-thbar" role="group" aria-label="Your threshold">
            <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }} aria-hidden="true">
              Your threshold
            </span>
            <span className="bz-mono" data-testid="threshold" style={{ fontWeight: 600, fontSize: 'var(--fs-lg)' }}>
              {formatScore(list.threshold)}
            </span>
            {kind && (
              <span className={`bz-pill${kind === 'Custom' ? ' bz-pill--predict' : ''}`} data-testid="threshold-kind">
                {kind}
              </span>
            )}
            <Link to={SETTINGS_PATH} className="bz-btn bz-btn--ghost bz-btn--sm">
              Change in Settings
            </Link>
          </div>
        </div>

        {list.status === 'loading' && list.bugs.length === 0 ? (
          <div className="text-center" style={{ padding: 40 }}>
            <Spinner color="primary" />
            <div className="bz-muted" style={{ marginTop: 8 }}>
              Loading the watch list…
            </div>
          </div>
        ) : list.status === 'failed' ? (
          <div style={{ padding: 16 }}>
            <div className="pl-empty">
              <div className="pl-empty__title">Predict data is unavailable right now.</div>
              <div className="bz-muted">Try again in a minute.</div>
              <button type="button" className="bz-btn bz-btn--sm" onClick={load}>
                Try again
              </button>
            </div>
          </div>
        ) : list.bugs.length === 0 ? (
          <div style={{ padding: 16 }}>
            <div className="pl-empty">
              <div className="pl-empty__title">No bugs on your watch list.</div>
              <div className="bz-muted">
                No bug in your scope scores {formatScore(list.threshold)} or higher yet. Predict scores new bugs once a
                day.
              </div>
              <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                {aboveDefault && (
                  <Link to={SETTINGS_PATH} className="bz-btn bz-btn--sm bz-btn--primary">
                    Reset to {formatScore(THRESHOLD_RANGE.default)} in Settings
                  </Link>
                )}
                <Link to="/predict/coverage" className="bz-btn bz-btn--sm">
                  See what Predict covers
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            <PredictProjectFilter
              projects={projects}
              total={list.bugs.length}
              selected={filters.projects}
              onChange={selected => setFilters(f => ({ ...f, projects: selected }))}
              aside={
                <span className="bz-muted" style={small} data-testid="shown-count">
                  {rows.length} {rows.length === 1 ? 'bug' : 'bugs'}
                  {belowPromotion > 0 ? ` · ${belowPromotion} below ${formatScore(PROMOTION_SCORE)}` : ''}
                  {list.asOf ? ` · updated ${formatDateTime(list.asOf)}` : ''}
                </span>
              }
            />
            {weaknesses.length > 0 && (
              <PredictProjectFilter
                title="Weakness type"
                noun="weakness type"
                nouns="weakness types"
                testId="weakness"
                projects={weaknesses}
                total={list.bugs.length}
                selected={filters.weaknesses}
                onChange={selected => setFilters(f => ({ ...f, weaknesses: selected }))}
              />
            )}

            {rows.length === 0 ? (
              <div style={{ padding: 16 }}>
                <div className="pl-empty">
                  <div className="pl-empty__title">No bugs match.</div>
                  <div className="bz-muted">Nothing matches this search, OSS, weakness type or minimum score.</div>
                  <button type="button" className="bz-btn bz-btn--sm" onClick={() => setFilters(NO_FILTERS)}>
                    Clear filters
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="bz-table" style={{ minWidth: 760 }}>
                    <thead>
                      <tr>
                        <SortHeader label="Score" sortKey="score" sort={sort} onSort={toggleSort} />
                        <th>Vendor</th>
                        <th>OSS</th>
                        <th>Bug</th>
                        <SortHeader label="Filed" sortKey="filed" sort={sort} onSort={toggleSort} />
                        <th>Rationale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((bug, index) => (
                        <React.Fragment key={bugKey(bug)}>
                          {first + index === band && (
                            <tr data-testid="band-row">
                              <td
                                colSpan={6}
                                className="bz-muted"
                                style={{
                                  ...small,
                                  background: 'var(--bg-soft)',
                                  borderTop: '1px dashed var(--line-strong)'
                                }}
                              >
                                Below {formatScore(PROMOTION_SCORE)}, not promoted. Shown because your threshold is{' '}
                                {formatScore(list.threshold)}.
                              </td>
                            </tr>
                          )}
                          <tr
                            data-testid="watchlist-row"
                            onClick={e => open(bug, e.currentTarget.querySelector('[data-detail-button]'))}
                            style={{ cursor: 'pointer' }}
                          >
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <ScoreChip score={bug.score} />
                              {!isPromoted(bug) && (
                                <span className="bz-muted" style={{ display: 'block', fontSize: 10, marginTop: 3 }}>
                                  Not promoted
                                </span>
                              )}
                            </td>
                            <td data-testid="inventory-vendor" style={{ minWidth: 120, maxWidth: 200 }}>
                              <InventoryVendor vendorId={bug.inventoryVendorId} products={bug.inventoryProducts} />
                            </td>
                            <td style={{ maxWidth: 200 }}>
                              <div className="bz-row" style={{ gap: 8 }}>
                                <VendorMark vendorId={bug.vendor} label={bug.targetName || bug.vendor} size={22} />
                                <span>{bug.targetName}</span>
                              </div>
                            </td>
                            <td style={{ minWidth: 220, maxWidth: 400 }}>
                              <div style={{ fontWeight: 500 }}>
                                <BugLink url={bug.url} onClick={e => e.stopPropagation()}>
                                  {bug.title}
                                </BugLink>
                              </div>
                              <div className="bz-row" style={{ ...small, gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                                <button
                                  type="button"
                                  className="bz-mono"
                                  style={linkButton}
                                  aria-label={`Open the detail of ${bug.bugId}`}
                                  data-detail-button
                                  onClick={e => {
                                    e.stopPropagation();
                                    open(bug, e.currentTarget);
                                  }}
                                >
                                  {bug.bugId}
                                </button>
                                <CvePill cve={bug.cve} />
                              </div>
                            </td>
                            <td className="bz-mono" style={{ ...small, whiteSpace: 'nowrap' }}>
                              {formatDate(bug.filed)}
                            </td>
                            <td className="bz-soft" style={{ ...small, minWidth: 220, maxWidth: 360 }}>
                              {bug.reason}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {rows.length > PAGE_SIZE && (
                  <div style={{ padding: '10px 16px', borderTop: '1px solid var(--line-soft)' }}>
                    <Pager
                      page={current}
                      pages={pages}
                      from={first + 1}
                      to={first + pageRows.length}
                      total={rows.length}
                      onPage={setPage}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {openBug && <BugPanel bug={openBug} threshold={list.threshold} onClose={close} />}
    </div>
  );
};

export default PredictWatchlist;
